import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { upperCase } from 'lodash'

import { useFormContext, useFieldArray } from 'react-hook-form'
import { Box, Typography, Divider, Button } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'

import { get } from 'utils/lodash'
import { sortByStringSelector } from 'data/utils/sortByStringSelector'
import { formatCentsToDollars, formatDecimalCentsToDollars } from 'utils/price'

import MaterialRow from './MaterialRow'
import { MATERIAL_FIELDS } from './settings'
import { getDisposalSiteDetails } from './disposal-fee/utils'

const MaterialField = ({ allSites = [], disposalFees = [] }) => {
  const { watch, control, getValues } = useFormContext()
  const { fields: materials, append, remove, update } = useFieldArray({ control, name: 'materials' })
  const { append: appendDeletedMaterials } = useFieldArray({ control, name: 'toBeDeletedMaterials' })
  const materialCount = get(materials, 'length', 0)
  const hasMultipleMaterials = materialCount > 1
  const disposalSiteId = watch('disposalSiteId')
  const disposalSiteDetails = useMemo(() => getDisposalSiteDetails(disposalSiteId, allSites), [disposalSiteId, allSites])
  const disposalSiteMaterials = useMemo(() => get(disposalSiteDetails, 'disposalSiteMaterial', []), [disposalSiteDetails])
  const sortedDisposalSiteMaterials = useMemo(
    () => sortByStringSelector({ data: disposalSiteMaterials, key: 'material.materialType' }),
    [disposalSiteMaterials]
  )

  const handleAddMaterial = () => {
    append({ ...MATERIAL_FIELDS }, { shouldFocus: false })
  }

  const handleDeleteMaterial = index => {
    const id = getValues(`materials.${index}.id`)
    if (id) {
      appendDeletedMaterials({ id, deleted: true })
    }
    remove(index)
  }

  const handleDisposalSiteMaterialIdChange = (newMaterialId, index) => {
    const currentMaterial = getValues(`materials.${index}`)
    const materialDetails = disposalSiteMaterials.find(material => material.id === newMaterialId)
    const uom = upperCase(get(materialDetails, 'measureUnit', ''))
    const disposalFee = disposalFees.find(fee => fee.materialId === materialDetails.material.id)
    const isNewMaterial = !currentMaterial.id

    const newMaterial = {
      ...currentMaterial,
      unitOfMeasure: uom,
      unitPriceDollars: get(materialDetails, 'tippingFee', ''),
      feeMaterialId: isNewMaterial ? get(disposalFee, 'id', '') : currentMaterial.feeMaterialId,
      // If no disposal fee is found, use the site rate
      useDisposalFeeRate: isNewMaterial ? !get(disposalFee, 'useSiteRate', true) : currentMaterial.useDisposalFeeRate,
      rate: isNewMaterial ? formatDecimalCentsToDollars(get(disposalFee, 'rate')) : currentMaterial.rate,
      includedTonnage: isNewMaterial ? get(disposalFee, 'includedTonnage', 0) : currentMaterial.includedTonnage,
      useMinimumCharge: isNewMaterial ? get(disposalFee, 'useMinimumCharge', false) : currentMaterial.useMinimumCharge,
      minimumCharge: isNewMaterial ? formatCentsToDollars(get(disposalFee, 'minimumChargeCents')) : currentMaterial.minimumCharge,
      overageWeight: isNewMaterial ? get(disposalFee, 'overageWeight', 0) : currentMaterial.overageWeight,
      overageFee: isNewMaterial ? formatCentsToDollars(get(disposalFee, 'overageFeeCents')) : currentMaterial.overageFee,
    }
    update(index, newMaterial)
  }

  return (
    <Box border="1px solid" borderColor="divider" borderRadius={1} p={2}>
      <Typography variant="h6" fontWeight={500} mb={2}>
        Materials
      </Typography>
      {materials.map((material, index) => {
        const id = get(material, 'id')

        return (
          <>
            <MaterialRow
              key={id}
              index={index}
              hasMultipleMaterials={hasMultipleMaterials}
              disposalSiteId={disposalSiteId}
              disposalSiteMaterials={sortedDisposalSiteMaterials}
              onDisposalSiteMaterialIdChange={newMaterialId => handleDisposalSiteMaterialIdChange(newMaterialId, index)}
              onDelete={() => handleDeleteMaterial(index)}
            />
            {index !== materials.length - 1 && <Divider sx={{ my: 2 }} />}
          </>
        )
      })}
      <Button variant="text" endIcon={<AddIcon />} onClick={handleAddMaterial}>
        Add material
      </Button>
    </Box>
  )
}

MaterialField.propTypes = {
  allSites: PropTypes.array,
  disposalFees: PropTypes.array,
}

export default MaterialField
